import clsx from 'clsx';
import { useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@tanstack/react-query';

import type { Scenario } from '@customTypes/external-resource';

import { fetchAbout } from '@services/AboutService';
import { fetchPreview } from '@services/HubService';

import Button from '@ui-components/input/Button';
import { Select, SelectItem } from '@ui-components/input/SelectInput';
import Icon from '@ui-components/layout/Icon';
import { PollingLoading } from '@ui-components/layout/LoadingIndicator';
import ParsedText from "@ui-components/layout/ParsedText";

import styles from './Personalization.module.css';
import { scenarioLabels, scenarios } from './scenarioLabels';

const messages = defineMessages({
  generateButton: {
    id: 'global.generateAnotherExample',
    defaultMessage: 'Generate another example',
    description: 'Button label to generate another preview example',
  },
});

type PersonalizationPreviewProps = {
  hubId: number;
  defaultLanguage?: string;
};

const PersonalizationPreview = ({ hubId, defaultLanguage }: PersonalizationPreviewProps) => {
  const intl = useIntl();
  const [scenario, setScenario] = useState<Scenario | ''>('');

  const { data: aboutData } = useQuery({
    queryKey: ['/about'],
    queryFn: fetchAbout,
  });

  const supportedLanguages = aboutData?.languages || [];
  const currentLanguageCode = defaultLanguage || 'en';
  const defaultLanguageName = supportedLanguages.find(l => l.code === currentLanguageCode)?.name;

  const previewMutation = useMutation({
    mutationFn: (selectedScenario: Scenario) => fetchPreview({ hubId, scenario: selectedScenario }),
  });

  const handleSelect = (value: string) => {
    setScenario(value as Scenario);
    previewMutation.mutate(value as Scenario);
  };

  const handleGeneratePreview = () => {
    if (scenario) previewMutation.mutate(scenario);
  };

  return (
    <div className={styles.previewContainer}>
      <FormattedMessage
        id="global.previewPersonalizations"
        defaultMessage="Preview personalizations"
        description="Title for preview personalizations"
        tagName="h2"
      />
      <FormattedMessage
        id="global.previewPersonalizationsDescription"
        defaultMessage="Select a scenario below to see an example message of how your chatbot would reply using your defined attributes & resources."
        description="Description about preview personalizations"
        tagName="p"
      />
      <Select
        label={
          <FormattedMessage
            id="global.previewDefaultLanguageInfo"
            defaultMessage="Replying in default language: {language}"
            description="Shows the default language in the preview panel"
            values={{
              language: defaultLanguageName || currentLanguageCode,
            }}
          />
        }
        helpContent={
          <FormattedMessage
            id="global.previewPersonalizationsDisclaimer"
            defaultMessage="Your chatbot may not respond exactly the same way each time."
            description="Disclaimer about preview personalizations"
          />
        }
        value={scenario}
        onValueChange={handleSelect}
        size="large"
        placeholder={
          <FormattedMessage
            id="global.previewPersonalizationsSelectScenario"
            defaultMessage="Select a scenario…"
            description="Description about selecting a scenario to preview personalizations"
          />
        }
      >
        <>
          {scenarios.map((option) => (
            <SelectItem key={option} value={option}>
              <FormattedMessage {...scenarioLabels[option]} />
            </SelectItem>
          ))}
        </>
      </Select>

      {previewMutation.isPending && (
        <div className={clsx(styles.previewMessage, styles.polling)}>
          <PollingLoading size="medium" className={styles.pollingLoading} />
        </div>
      )}

      {previewMutation.data && !previewMutation.isPending && (
        <div className={styles.previewMessage}>
          <p><ParsedText text={previewMutation.data} /></p>
        </div>
      )}

      {previewMutation.isError && (
        <div className={styles.previewMessage}>
          <p role="alert">
            <FormattedMessage
              id="global.previewPersonalizationsError"
              defaultMessage="It was not possible to generate an example message. Please try again."
              description="Shown in the personalization preview panel when the example chatbot message could not be generated"
            />
          </p>
        </div>
      )}

      {scenario && (
        <Button
          label={intl.formatMessage(messages.generateButton)}
          iconLeft={<Icon id="arrow_clockwise" />}
          onClick={handleGeneratePreview}
          disabled={previewMutation.isPending}
          className={styles.generateButton}
          variant="filled"
          size="small"
          themeMode
        />
      )}
    </div>
  );
};

export default PersonalizationPreview;
